import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { isAuthenticated, logout, getCurrentUser } from '../utils/api';
import { 
  IoGameController, 
  IoPersonOutline, 
  IoLogOutOutline, 
  IoMenuOutline,
  IoCloseOutline,
  IoPersonAddOutline,
  IoHomeOutline,
  IoTrophyOutline,
  IoDocumentTextOutline,
  IoBookOutline,
  IoMenuSharp,
  IoVideocamOutline
} from 'react-icons/io5';
import AdminSidebar from './AdminSidebar';
import './Header.css';

const Header = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [adminSidebarOpen, setAdminSidebarOpen] = useState(false);

  // Check authentication status on component mount and auth changes
  useEffect(() => {
    const checkAuth = () => {
      const authStatus = isAuthenticated();
      const currentUser = getCurrentUser();
      setAuthenticated(authStatus);
      setUser(currentUser);
    };

    // Check initial authentication status
    checkAuth();

    // Listen for storage events (when localStorage changes in other tabs)
    const handleStorageChange = () => {
      checkAuth();
    };

    // Listen for custom auth change events (same tab)
    const handleAuthChange = () => {
      checkAuth();
    };

    // Handle scroll events for header styling
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authChange', handleAuthChange);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChange', handleAuthChange);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const isAdmin = user?.role_id === 1;
  const isGameShowUser = user?.role_id === 3;

  const handleLogout = () => {
    logout();
    setAuthenticated(false);
    setUser(null);
    setMobileMenuOpen(false);
    setAdminSidebarOpen(false);
  };

  const toggleMobileMenu = (e) => {
    e.stopPropagation(); // Prevent event from bubbling
    setMobileMenuOpen(!mobileMenuOpen);
    // Close admin sidebar when opening mobile menu
    if (!mobileMenuOpen) {
      setAdminSidebarOpen(false);
    }
  };

  const toggleAdminSidebar = (e) => {
    e.stopPropagation(); // Prevent event from bubbling
    e.preventDefault(); // Prevent any default actions
    setAdminSidebarOpen(!adminSidebarOpen);
    // Close mobile menu when opening admin sidebar
    if (!adminSidebarOpen) {
      setMobileMenuOpen(false);
    }
  };

  // Close mobile menu and admin sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideHeader = event.target.closest('.header');
      const isClickInsideAdminSidebar = event.target.closest('.admin-sidebar');
      const isClickAdminToggle = event.target.closest('.admin-sidebar-toggle');
      const isClickMobileMenuBtn = event.target.closest('.mobile-menu-btn');
      
      // Don't close if clicking inside these elements
      if (isClickInsideHeader || isClickInsideAdminSidebar || isClickAdminToggle || isClickMobileMenuBtn) {
        return;
      }

      setMobileMenuOpen(false);
      setAdminSidebarOpen(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <>
      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="header-container">
          {/* Admin Sidebar Toggle Button */}
          {isAdmin && (
            <button 
              className="admin-sidebar-toggle"
              onClick={toggleAdminSidebar}
              aria-label="Toggle admin sidebar"
              aria-expanded={adminSidebarOpen}
            >
              <IoMenuSharp className="admin-sidebar-toggle-icon" />
            </button>
          )}

          {/* Logo/Brand Section */}
          <div className="header-brand">
            <Link to="/" className="brand-link" onClick={() => {
              setMobileMenuOpen(false);
              setAdminSidebarOpen(false);
            }}>
              <IoGameController className="brand-icon" />
              <span className="brand-text">AI Game Show</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            <ul className="nav-list">
              {!authenticated ? (
                <>
                  <li>
                    <Link to="/" className="nav-link">
                      <IoHomeOutline />
                      <span>Home</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/how-it-works" className="nav-link">
                      <IoBookOutline />
                      <span>How It Works</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/live-stream" className="nav-link">
                      <IoVideocamOutline />
                      <span>Live Stream</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/register" className="nav-link register-btn">
                      <IoPersonAddOutline />
                      <span>Register</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/login" className="nav-link login-btn">
                      <IoPersonOutline />
                      <span>Login</span>
                    </Link>
                  </li>
                </>
              ) : isGameShowUser ? (
                <>
                  <li>
                    <Link to="/" className="nav-link">
                      <IoDocumentTextOutline />
                      <span>Answer Questions</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/how-it-works" className="nav-link">
                      <IoBookOutline />
                      <span>How It Works</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/live-stream" className="nav-link">
                      <IoVideocamOutline />
                      <span>Live Stream</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/my-answer" className="nav-link">
                      <IoDocumentTextOutline />
                      <span>My Answers</span>
                    </Link>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <Link to="/" className="nav-link">
                      <IoHomeOutline />
                      <span>Home</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/how-it-works" className="nav-link">
                      <IoBookOutline />
                      <span>How It Works</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/live-stream" className="nav-link">
                      <IoVideocamOutline />
                      <span>Live Stream</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/my-answer" className="nav-link">
                      <IoDocumentTextOutline />
                      <span>My Answers</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/leaderboard" className="nav-link">
                      <IoTrophyOutline />
                      <span>Leaderboard</span>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </nav>

          {/* User Section */}
          {authenticated && (
            <div className="user-section">
              <Link to="/profile" className="welcome-message">
                <IoPersonOutline className="welcome-icon" />
                <span className="welcome-text">
                  Welcome, {user.name}
                  {isGameShowUser && <span className="user-badge">Game Show Player</span>}
                </span>
              </Link>
              <button onClick={handleLogout} className="logout-btn">
                <IoLogOutOutline className="logout-icon" />
                <span>Logout</span>
              </button>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <IoCloseOutline className="mobile-menu-icon" />
            ) : (
              <IoMenuOutline className="mobile-menu-icon" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        <nav className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`} aria-hidden={!mobileMenuOpen}>
          <ul className="mobile-nav-list">
            {!authenticated ? (
              <>
                <li>
                  <Link to="/" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                    <IoHomeOutline />
                    <span>Home</span>
                  </Link>
                </li>
                <li>
                  <Link to="/how-it-works" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                    <IoBookOutline />
                    <span>How It Works</span>
                  </Link>
                </li>
                <li>
                  <Link to="/live-stream" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                    <IoVideocamOutline />
                    <span>Live Stream</span>
                  </Link>
                </li>
                <li>
                  <Link to="/register" className="mobile-nav-link register-btn" onClick={() => setMobileMenuOpen(false)}>
                    <IoPersonAddOutline />
                    <span>Register</span>
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="mobile-nav-link login-btn" onClick={() => setMobileMenuOpen(false)}>
                    <IoPersonOutline />
                    <span>Login</span>
                  </Link>
                </li>
              </>
            ) : isGameShowUser ? (
              <>
                <li>
                  <Link to="/" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                    <IoDocumentTextOutline />
                    <span>Answer Questions</span>
                  </Link>
                </li>
                <li>
                  <Link to="/how-it-works" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                    <IoBookOutline />
                    <span>How It Works</span>
                  </Link>
                </li>
                <li>
                  <Link to="/live-stream" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                    <IoVideocamOutline />
                    <span>Live Stream</span>
                  </Link>
                </li>
                <li>
                  <Link to="/my-answer" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                    <IoDocumentTextOutline />
                    <span>My Answers</span>
                  </Link>
                </li>
                <li>
                  <div className="mobile-welcome">
                    <IoPersonOutline className="mobile-welcome-icon" />
                    <span>Welcome, {user.name}</span>
                    <span className="user-badge">Game Show Player</span>
                  </div>
                </li>
                <li>
                  <button onClick={handleLogout} className="mobile-logout-btn">
                    <IoLogOutOutline className="mobile-logout-icon" />
                    <span>Logout</span>
                  </button>
                </li>
              </>
            ) : (
              <>
                <li>
                  <Link to="/" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                    <IoHomeOutline />
                    <span>Home</span>
                  </Link>
                </li>
                <li>
                  <Link to="/how-it-works" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                    <IoBookOutline />
                    <span>How It Works</span>
                  </Link>
                </li>
                <li>
                  <Link to="/live-stream" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                    <IoVideocamOutline />
                    <span>Live Stream</span>
                  </Link>
                </li>
                <li>
                  <Link to="/my-answer" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                    <IoDocumentTextOutline />
                    <span>My Answers</span>
                  </Link>
                </li>
                <li>
                  <Link to="/leaderboard" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                    <IoTrophyOutline />
                    <span>Leaderboard</span>
                  </Link>
                </li>
                <li>
                  <div className="mobile-welcome">
                    <IoPersonOutline className="mobile-welcome-icon" />
                    <span>Welcome, {user.name}</span>
                  </div>
                </li>
                <li>
                  <button onClick={handleLogout} className="mobile-logout-btn">
                    <IoLogOutOutline className="mobile-logout-icon" />
                    <span>Logout</span>
                  </button>
                </li>
              </>
            )}
          </ul>
        </nav>
      </header>

      {/* Admin Sidebar */}
      {isAdmin && (
        <AdminSidebar 
          isOpen={adminSidebarOpen} 
          onClose={(e) => {
            if (e) {
              e.stopPropagation();
            }
            setAdminSidebarOpen(false);
          }} 
        />
      )}
    </>
  );
};

export default Header; 